import { createContext, useCallback, useContext, useMemo, useState, useRef, PropsWithChildren, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import noop from 'lodash/noop';
import omitBy from 'lodash/omitBy';
import isNil from 'lodash/isNil';
import { usePostHog } from 'posthog-js/react';
import { API_ROUTES } from '@/api/apiRoutes';
import { safeAxios } from '@/services/safeAxios';
import { log } from '@/logger/logger.ts';
import { AuthContext } from '@/contexts/AuthContext';
import { UserTourStatus } from '@/enums/user-tour-status.enum';
import { I18N_KEYS } from '@/language/keys';
import { asyncNoop } from '@/utils/functions';
import { useConfirmationDialogStore } from '@/state/confirmation-dialog.state';
import untypedTourConfigs from './tourConfigs.json';
import { Tour } from './Tour';
import { TourKeys } from './tour-keys';
import { enqueueTourUpdate } from './tourUpdateQueue';
import type { TourDetails, User } from '@/types/auth.types';
import type {
  HookFunction,
  TourAction,
  TourConfig,
  TourConfigs,
  TourContextStore,
  TourCustomDataContext,
} from './tourTypes';

const logger = log.getLogger('TourContext');

export const tourConfigs = untypedTourConfigs as TourConfigs;

export const TourContext = createContext<TourContextStore>({
  activeTour: null,
  allowBack: false,
  callAction: asyncNoop,
  canMoveToNextStep: false,
  checkForEligibleTour: () => null,
  context: undefined,
  currentStep: 0,
  endTour: noop,
  getCurrentStepConfig: () => null,
  hiddenTooltipStepId: null,
  hideTooltip: noop,
  goToNextStep: asyncNoop,
  goToPreviousStep: noop,
  goToStep: noop,
  goToStepById: asyncNoop,
  markTourCompleted: asyncNoop,
  updateTour: asyncNoop,
  onSuccessAnimationEnd: noop,
  quitTour: noop,
  registerPostHookFunction: noop,
  registerPreHookFunction: noop,
  runTour: asyncNoop,
  setActiveComponentId: noop,
  setTourHidden: noop,
  showAnimation: false,
  steps: [],
  stopTour: noop,
  subscribeToTourQuit: noop,
  tourStatus: undefined,
  updateCanMoveToNextStep: noop,
  updateCustomData: noop,
});

export const TourProvider = ({ children }: PropsWithChildren) => {
  const { t } = useTranslation();
  const { currentUser } = useContext(AuthContext);
  const [activeComponentId, setActiveComponentId] = useState<string>();
  const [activeTour, setActiveTour] = useState<TourKeys | null>(null);
  const [canMoveToNextStepByTour, setCanMoveToNextStepByTour] = useState<Partial<Record<TourKeys, boolean>>>({});
  const [context, setContext] = useState<TourCustomDataContext>();
  const [currentStep, setCurrentStep] = useState(0);
  const [customData, setCustomData] = useState<TourDetails['customData']>();
  const [isTourHidden, setTourHidden] = useState(false);
  const [hiddenTooltipStepId, setHiddenTooltipStepId] = useState<string | null>(null);
  const [postHookFunctions, setPostHookFunctions] = useState<Record<string, HookFunction>>({});
  const [preHookFunctions, setPreHookFunctions] = useState<Record<string, HookFunction>>({});
  const [showAnimation, setShowAnimation] = useState(false);
  const [tourStatus, setTourStatus] = useState<UserTourStatus>();
  const tourUpdateQueuesRef = useRef(new Map<TourKeys, Promise<void>>());
  const isMovingStepRef = useRef(false);
  const onSuccessAnimationEndRef = useRef(noop);
  const timeoutRef = useRef<NodeJS.Timeout>(undefined);
  const posthog = usePostHog();
  const [onQuitTourSubscribers, setOnQuitTourSubscribers] = useState<Record<string, () => void>>({});
  const { openConfirmationDialog } = useConfirmationDialogStore();

  const canMoveToNextStep = activeTour ? canMoveToNextStepByTour[activeTour] ?? true : false;

  const setCanMoveToNextStep = useCallback((tourId: TourKeys, canMove: boolean) => {
    setCanMoveToNextStepByTour((prev) => ({ ...prev, [tourId]: canMove }));
  }, []);

  const hideTooltip = useCallback((stepId: string) => {
    setHiddenTooltipStepId(stepId);
  }, []);

  useEffect(() => {
    setHiddenTooltipStepId(null);
  }, [activeTour, currentStep]);

  const subscribeToTourQuit = useCallback((id: string, func: () => void) => {
    setOnQuitTourSubscribers((prev) => ({ ...prev, [id]: func }));
  }, []);

  const onSuccessAnimationEnd = useCallback(() => {
    setShowAnimation(false);
    onSuccessAnimationEndRef.current();
    onSuccessAnimationEndRef.current = noop;
  }, []);

  const allowBack = useMemo(() => {
    if (!activeTour || !tourConfigs[activeTour]) return false;
    return tourConfigs[activeTour].allowBack ?? false;
  }, [activeTour]);

  const sendTourUpdate = useCallback(async (tourId: TourKeys, data: Partial<TourDetails>) => {
    try {
      await safeAxios.put(API_ROUTES.users.tour({ tourId }), omitBy(data, isNil));
    } catch (error) {
      logger.error('Error updating user tour', error);
    }
  }, []);

  const updateTour = useCallback(
    async (tourId: TourKeys, data: Partial<TourDetails>) => {
      await enqueueTourUpdate(tourUpdateQueuesRef.current, tourId, () => sendTourUpdate(tourId, data));
    },
    [sendTourUpdate],
  );

  const hookRunner = useCallback(
    async (tourId: TourKeys, stepIndex: number, hookType: 'preHook' | 'postHook') => {
      const nextStepConfig = tourConfigs[tourId].steps[stepIndex];
      if (hookType in nextStepConfig && nextStepConfig[hookType]) {
        const hookFunction =
          hookType === 'preHook'
            ? preHookFunctions[nextStepConfig[hookType]]
            : postHookFunctions[nextStepConfig[hookType]];

        if (!hookFunction) {
          logger.error(`${hookType} function "${nextStepConfig[hookType]}" not registered`);
          return; // Don't proceed if hook function is missing
        }

        try {
          await hookFunction();
        } catch (error) {
          logger.error(`Error executing ${hookType}.`, error);
        }
      }
    },
    [preHookFunctions, postHookFunctions],
  );

  const runPreHook = useCallback(
    async (tourId: TourKeys, stepIndex: number) => {
      await hookRunner(tourId, stepIndex, 'preHook');
    },
    [hookRunner],
  );

  const runPostHook = useCallback(
    async (tourId: TourKeys, stepIndex: number) => {
      await hookRunner(tourId, stepIndex, 'postHook');
    },
    [hookRunner],
  );

  const runActionHook = useCallback(
    async (hookName: string) => {
      const hookFunction = postHookFunctions[hookName];
      if (!hookFunction) {
        logger.error(`Action hook "${hookName}" not registered`);
        return;
      }
      try {
        await hookFunction();
      } catch (error) {
        logger.error('Error executing action hook.', error);
      }
    },
    [postHookFunctions],
  );

  const getEligibleTours = useCallback(
    (componentId: string, userPendingTours?: User['tours']): [TourKeys, TourConfig][] => {
      if (!userPendingTours || Object.keys(userPendingTours).length === 0) return [];

      // Object entries cannot accept typed keys, so we need to cast to [TourKeys, TourConfig]
      return (Object.entries(tourConfigs) as [TourKeys, TourConfig][])
        .filter(([tourId, config]) => {
          // TRANSITION (WEA-4892): the welcome tour is started by useWelcomeTemplateGalleryTour's
          // flag-gated override, not by automatic selection. Remove this exclusion when the flag ships
          // and the navigation-tour config is replaced with the welcome content.
          if (tourId === TourKeys.WelcomeTemplateGallery) return false;
          if (config.kind === 'announcement') return false;
          return config.componentId === componentId && userPendingTours[tourId];
        })
        .sort((a, b) => (a[1].priority ?? Infinity) - (b[1].priority ?? Infinity));
    },
    [],
  );

  const findActiveTourIndex = useCallback((eligibleTours: [TourKeys, TourConfig][], activeTourId: TourKeys) => {
    return eligibleTours.findIndex(([tourId]) => tourId === activeTourId);
  }, []);

  const checkForEligibleTour = useCallback(
    (componentId?: string) => {
      const componentIdToUse = componentId || activeComponentId;
      if (!currentUser?.tours || Object.keys(currentUser.tours).length === 0 || !componentIdToUse) return null;
      const eligibleTours = getEligibleTours(componentIdToUse, currentUser.tours);

      const tourToShow = eligibleTours?.[0];
      if (!tourToShow) return null;
      const userDataTour = currentUser.tours[tourToShow[0]];
      const pendingTourCurrentStep = userDataTour?.currentStep;

      if (tourToShow && pendingTourCurrentStep !== undefined && userDataTour.status !== UserTourStatus.COMPLETED) {
        return {
          tourId: tourToShow[0],
          currentStep: pendingTourCurrentStep,
          customData: userDataTour.customData,
        };
      }
      return null;
    },
    [activeComponentId, currentUser?.tours, getEligibleTours],
  );

  const updateServerOnTourEndOrQuit = useCallback(
    async (tourId: TourKeys) => {
      try {
        setTourStatus(UserTourStatus.COMPLETED);
        await updateTour(tourId, { status: UserTourStatus.COMPLETED });
      } catch (error) {
        logger.error('Error updating user tour', error);
      }
    },
    [updateTour],
  );

  // Persist a tour as COMPLETED without touching the active tour. Used to retire `navigation-tour` when
  // the welcome-template-gallery flow runs instead, and to finish that tour (with stopTour) without
  // chaining into the next eligible tour. Best-effort: harmless if the server doesn't know the key yet.
  const markTourCompleted = useCallback(
    async (tourId: TourKeys) => {
      try {
        await updateTour(tourId, { status: UserTourStatus.COMPLETED });
      } catch (error) {
        logger.error('Error completing user tour', error);
      }
    },
    [updateTour],
  );

  const stopTour = useCallback(() => {
    setActiveTour(null);
    setCurrentStep(0);
    setTourStatus(undefined);
    Object.values(onQuitTourSubscribers).forEach((subscriber) => subscriber());
  }, [onQuitTourSubscribers]);

  const handleQuitTour = useCallback(
    (tourId: TourKeys, outcome: 'completed' | 'aborted' = 'aborted') => {
      posthog.capture('product_tour_end', {
        tour: tourId,
        outcome,
        step: currentStep,
        stepId: tourConfigs[tourId]?.steps[currentStep]?.stepId,
      });
      // Stop immediately so the UI (e.g. closing the tour modal) doesn't wait on the network; persist
      // completion in the background (best-effort, like markTourCompleted).
      stopTour();
      void updateServerOnTourEndOrQuit(tourId);
    },
    [posthog, updateServerOnTourEndOrQuit, stopTour, currentStep],
  );

  const registerPreHookFunction = useCallback((functionName: string, handler: HookFunction) => {
    setPreHookFunctions((prev) => ({
      ...prev,
      [functionName]: handler,
    }));
  }, []);

  const registerPostHookFunction = useCallback((functionName: string, handler: HookFunction) => {
    setPostHookFunctions((prev) => ({
      ...prev,
      [functionName]: handler,
    }));
  }, []);

  const updateContext = useCallback(
    (newContext: TourCustomDataContext, allowUpdateTour = true) => {
      if (!activeTour && !allowUpdateTour) return;
      setContext((prev) => {
        if (!prev) return newContext;
        return { ...prev, ...newContext };
      });
      const tourId = activeTour || (Object.keys(newContext)[0] as TourKeys);
      const newCustomData = { ...customData, context: newContext[tourId] };
      void updateTour(tourId, { customData: newCustomData });
      setCustomData(newCustomData);
    },
    [activeTour, customData, updateTour],
  );

  const updateCustomData = useCallback(
    (newCustomData: TourDetails['customData']) => {
      if (!activeTour) return;
      setCustomData(newCustomData);
      void updateTour(activeTour, { customData: newCustomData });
    },
    [activeTour, updateTour],
  );

  const runTour = useCallback(
    async (tourId: TourKeys, step: number, customData?: TourDetails['customData']) => {
      if (tourConfigs[tourId]) {
        await runPreHook(tourId, step);
        setActiveTour(tourId);
        setCurrentStep(step);
        setCanMoveToNextStep(tourId, tourConfigs[tourId].steps[step].noTask ?? true);
        setTourStatus(UserTourStatus.IN_PROGRESS);
        if (customData) {
          setCustomData(customData);
          if (customData.context) {
            updateContext({ [tourId]: customData.context });
          }
        }
      }
    },
    [runPreHook, setCanMoveToNextStep, updateContext],
  );

  const goToNextTour = useCallback(async () => {
    const activeTourBeforeQuit = activeTour;
    if (!activeTourBeforeQuit || !activeComponentId) return;
    handleQuitTour(activeTourBeforeQuit, 'completed');
    const eligibleTours = getEligibleTours(activeComponentId, currentUser?.tours);
    // Only chain to the next tour when the finishing tour is part of the eligible chain; a tour started
    // outside automatic selection (e.g. the welcome onboarding tour) isn't, so it shouldn't pull in one.
    const activeTourIndex = findActiveTourIndex(eligibleTours, activeTourBeforeQuit);
    const nextTour = activeTourIndex === -1 ? undefined : eligibleTours[activeTourIndex + 1];
    if (nextTour && nextTour[0]) {
      setActiveTour(nextTour[0]);
      setCurrentStep(0);
      await runTour(nextTour[0], 0);
    }
  }, [
    activeTour,
    activeComponentId,
    currentUser?.tours,
    findActiveTourIndex,
    getEligibleTours,
    handleQuitTour,
    runTour,
  ]);

  const goToStep = useCallback(
    (stepIndex: number) => {
      if (!activeTour || !tourConfigs[activeTour]) return;
      if (stepIndex >= 0 && stepIndex < tourConfigs[activeTour].steps.length) {
        void runPreHook(activeTour, stepIndex);
        setCurrentStep(stepIndex);
        setCanMoveToNextStep(activeTour, tourConfigs[activeTour].steps[stepIndex].noTask ?? true);
        void updateTour(activeTour, {
          status: UserTourStatus.IN_PROGRESS,
          currentStep: stepIndex,
          customData,
        });
      }
    },
    [activeTour, customData, runPreHook, setCanMoveToNextStep, updateTour],
  );

  /** Awaits the target step's preHook before showing it, so its anchor exists when the tooltip mounts. */
  const goToStepById = useCallback(
    async (stepId: string) => {
      if (!activeTour || !tourConfigs[activeTour]) return;
      const stepIndex = tourConfigs[activeTour].steps.findIndex((step) => step.stepId === stepId);
      if (stepIndex === -1) return;

      await runPreHook(activeTour, stepIndex);
      setCurrentStep(stepIndex);
      setCanMoveToNextStep(activeTour, tourConfigs[activeTour].steps[stepIndex].noTask ?? true);
      void updateTour(activeTour, {
        status: UserTourStatus.IN_PROGRESS,
        currentStep: stepIndex,
        customData,
      });
    },
    [activeTour, customData, runPreHook, setCanMoveToNextStep, updateTour],
  );

  const goToNextStep = useCallback(async () => {
    if (!activeTour || !tourConfigs[activeTour]) return;
    if (currentStep < tourConfigs[activeTour].steps.length - 1) {
      void runPostHook(activeTour, currentStep);
      // Await the next step's preHook so any DOM it prepares (e.g. opening a drawer and tagging the
      // tour anchor) exists before the step renders — Tour resolves the anchor once at render, so a
      // late imperative tag would otherwise leave the tooltip stuck at its fallback position. This
      // mirrors runTour, which already awaits runPreHook before showing a step.
      await runPreHook(activeTour, currentStep + 1);
      setCurrentStep((prev) => prev + 1);
      setCanMoveToNextStep(activeTour, tourConfigs[activeTour].steps[currentStep + 1].noTask ?? true);
      void updateTour(activeTour, {
        status: UserTourStatus.IN_PROGRESS,
        currentStep: currentStep + 1,
        customData,
      });
    } else {
      // End tour if we're at the last step
      await goToNextTour();
    }
  }, [
    activeTour,
    currentStep,
    customData,
    goToNextTour,
    runPostHook,
    runPreHook,
    setCanMoveToNextStep,
    updateTour,
  ]);

  const goToPreviousStep = useCallback(() => {
    if (!activeTour || !tourConfigs[activeTour]) return;
    if (currentStep > 0) {
      void runPreHook(activeTour, currentStep - 1);
      setCurrentStep((prev) => prev - 1);
      setCanMoveToNextStep(activeTour, tourConfigs[activeTour].steps[currentStep - 1].noTask ?? true);
      void updateTour(activeTour, {
        status: UserTourStatus.IN_PROGRESS,
        currentStep: currentStep - 1,
        customData,
      });
    }
  }, [activeTour, currentStep, customData, runPreHook, setCanMoveToNextStep, updateTour]);

  const updateCanMoveToNextStep = useCallback(
    (newCanMoveToNextStep: boolean) => {
      if (!activeTour || !tourConfigs[activeTour]) return;
      setCanMoveToNextStep(activeTour, newCanMoveToNextStep);
      if (newCanMoveToNextStep) {
        const autoPass = tourConfigs[activeTour].steps[currentStep].autoPass;
        if (autoPass && !isMovingStepRef.current) {
          isMovingStepRef.current = true;
          const delayedMoveStep = async () => {
            await goToNextStep();
            isMovingStepRef.current = false;
          };
          if (tourConfigs[activeTour].steps[currentStep].showSuccessAnimation) {
            onSuccessAnimationEndRef.current = () => void delayedMoveStep();
            setShowAnimation(true);
          } else {
            // run the next step after half a second delay
            timeoutRef.current = setTimeout(() => void delayedMoveStep(), 500);
          }
        }
      }
    },
    [activeTour, currentStep, goToNextStep, setCanMoveToNextStep],
  );

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const endTour = useCallback(() => {
    if (!activeTour) return;

    if (tourConfigs[activeTour].steps[currentStep].noTask) {
      return handleQuitTour(activeTour, 'aborted');
    }
    openConfirmationDialog({
      title: t(I18N_KEYS.PRODUCT_TOURS.QUIT_TOUR_CONFIRMATION_TITLE),
      message: t(I18N_KEYS.PRODUCT_TOURS.QUIT_TOUR_CONFIRMATION_MESSAGE),
      cancelBtnText: t(I18N_KEYS.PRODUCT_TOURS.QUIT_TOUR_CONFIRMATION_CANCEL),
      confirmBtnText: t(I18N_KEYS.PRODUCT_TOURS.QUIT_TOUR_CONFIRMATION_CTA),
      width: 'md',
      onConfirm: () => handleQuitTour(activeTour, 'aborted'),
    });
  }, [activeTour, handleQuitTour, currentStep, openConfirmationDialog, t]);

  const callAction = useCallback(
    async (action: TourAction) => {
      if (!activeTour) return;
      const stepConfig = tourConfigs[activeTour]?.steps[currentStep];
      posthog.capture('product_tour_action_clicked', {
        tour: activeTour,
        step: currentStep,
        stepId: stepConfig?.stepId,
        action: action.onClick,
        label: action.label,
      });
      if (action.hook) await runActionHook(action.hook);
      if (action.onClick === 'setContext') {
        if (!action.context) return;
        updateContext({ [activeTour]: action.context });
        await goToNextStep();
      } else if (action.onClick === 'nextTour') {
        await goToNextTour();
      } else if (action.onClick === 'closeTour') {
        stopTour();
      } else if (action.onClick === 'endTour') {
        handleQuitTour(activeTour, 'completed');
      } else if (action.onClick === 'cancelTour') {
        handleQuitTour(activeTour, 'aborted');
      } else if (action.onClick === 'nextStep') {
        await goToNextStep();
      } else if (action.onClick === 'previousStep') {
        goToPreviousStep();
      }
    },
    [
      activeTour,
      currentStep,
      goToNextStep,
      goToNextTour,
      goToPreviousStep,
      handleQuitTour,
      posthog,
      runActionHook,
      stopTour,
      updateContext,
    ],
  );

  const steps = useMemo(() => {
    if (!activeTour) return [];
    return tourConfigs[activeTour].steps;
  }, [activeTour]);

  const getCurrentStepConfig = useCallback(() => {
    if (!steps.length) return null;
    return steps[currentStep];
  }, [currentStep, steps]);

  const value: TourContextStore = useMemo(
    () => ({
      activeTour,
      allowBack,
      callAction,
      canMoveToNextStep,
      checkForEligibleTour,
      context,
      currentStep,
      endTour,
      getCurrentStepConfig,
      hiddenTooltipStepId,
      hideTooltip,
      goToNextStep,
      goToPreviousStep,
      goToStep,
      goToStepById,
      markTourCompleted,
      updateTour,
      onSuccessAnimationEnd,
      quitTour: handleQuitTour,
      registerPostHookFunction,
      registerPreHookFunction,
      runTour,
      setActiveComponentId,
      setTourHidden,
      showAnimation,
      steps,
      stopTour,
      subscribeToTourQuit,
      tourStatus,
      updateCanMoveToNextStep,
      updateCustomData,
    }),
    [
      activeTour,
      allowBack,
      callAction,
      canMoveToNextStep,
      checkForEligibleTour,
      context,
      currentStep,
      endTour,
      getCurrentStepConfig,
      hiddenTooltipStepId,
      hideTooltip,
      goToNextStep,
      goToPreviousStep,
      goToStep,
      goToStepById,
      handleQuitTour,
      markTourCompleted,
      updateTour,
      onSuccessAnimationEnd,
      registerPostHookFunction,
      registerPreHookFunction,
      runTour,
      showAnimation,
      steps,
      stopTour,
      subscribeToTourQuit,
      tourStatus,
      updateCanMoveToNextStep,
      updateCustomData,
    ],
  );

  return (
    <>
      <TourContext.Provider value={value}>
        {activeTour && !isTourHidden ? <Tour /> : null}
        {children}
      </TourContext.Provider>
    </>
  );
};

export const useTour = (): TourContextStore => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

/**
 * Hides the tour UI while `hidden` is true without ending or advancing the tour; it re-appears on the
 * same step once `hidden` flips back or the caller unmounts. For full-screen surfaces that cover the
 * canvas a tour points at, where stacking the tour above or below the surface is wrong either way.
 */
export const useHideTourWhile = (hidden: boolean) => {
  const { setTourHidden } = useTour();

  useEffect(() => {
    if (!hidden) return;
    setTourHidden(true);
    return () => setTourHidden(false);
  }, [hidden, setTourHidden]);
};
